import React from 'react';
import { FaShieldAlt, FaAward, FaUsers, FaChartBar, FaGem, FaRocket, FaChartLine, FaGlobe, FaBullhorn, FaHandshake, FaCrown } from 'react-icons/fa';
import { motion } from 'framer-motion';

const AboutCompany = ({ contentData }) => {
  const stats = [
    { 
      icon: <FaShieldAlt />, 
      value: contentData?.amm_card1_title, 
      label: contentData?.amm_card1_text, 
      color: "blue",
      animation: "animate-bounce-slow"
    },
    { 
      icon: <FaUsers />, 
      value: contentData?.amm_card2_title, 
      label: contentData?.amm_card2_text, 
      color: "purple",
      animation: "animate-pulse animation-delay-500"
    },
    { 
      icon: <FaAward />, 
      value: contentData?.amm_card3_title, 
      label: contentData?.amm_card3_text, 
      color: "green",
      animation: "animate-float"
    },
    { 
      icon: <FaChartBar />, 
      value: contentData?.amm_card4_title, 
      label: contentData?.amm_card4_text, 
      color: "orange",
      animation: "animate-pulse animation-delay-1000"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden" id='about'>
      {/* Background Animated Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Floating Financial Symbols */}
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 360, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/4 left-10 text-5xl opacity-5"
        >
          ₹
        </motion.div>
        
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/3 right-20 text-6xl opacity-5"
        >
          📈
        </motion.div>
        
        {/* Animated Circles */}
        <div className="absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [0.1, 0.3, 0.1]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                delay: i * 0.5,
                ease: "easeInOut"
              }}
              className={`absolute w-${(i+1)*10} h-${(i+1)*10} ${
                i % 2 === 0 ? 'bg-blue-400' : 'bg-purple-400'
              } rounded-full opacity-10`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
        
        {/* Animated Chart Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-5">
          <motion.path
            d="M0,100 Q150,50 300,150 Q450,200 600,100 Q750,50 900,150"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          <motion.path
            d="M0,250 Q200,200 400,300 Q600,250 800,200"
            fill="none"
            stroke="#8b5cf6"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
          />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Animated Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          {/* Animated Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-300 px-6 py-3 rounded-full mb-6 shadow-lg"
          >
            <FaCrown className="text-yellow-500 animate-pulse" />
            <span className="text-blue-700 font-bold">TRUSTED BY INVESTORS NATIONWIDE</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            About Market Maestroo
          </h2>
          
          {/* Animated Underline */}
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "120px" }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-4 rounded-full"
          />
          
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            SEBI Registered Research Analyst firm trusted by investors across India
          </p>
        </motion.div>

        {/* Animated Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-5">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
              viewport={{ once: true }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)"
              }}
              className={`relative overflow-hidden bg-white p-6 rounded-xl shadow-lg border-t-4 border-${stat.color}-500 group`}
            >
              {/* Animated Background Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <div className={`${stat.animation} text-${stat.color}-500 text-2xl mb-4 relative z-10`}>
                {stat.icon}
              </div>
              
              <div className="text-2xl font-bold text-gray-900 mb-1 relative z-10">{stat.value}</div>
              <div className="text-gray-600 text-sm relative z-10">{stat.label}</div>
              
              {/* Animated Dots */}
              <div className="absolute -bottom-2 -right-2 opacity-10">
                <div className="flex space-x-1">
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      className="w-2 h-2 bg-blue-400 rounded-full"
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Company Info with Enhanced Animations */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Floating Element */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-r from-blue-200 to-cyan-200 rounded-full opacity-20 blur-xl"
            />
            
            <div className="bg-white p-8 rounded-2xl shadow-lg relative overflow-hidden group">
              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-6 relative z-10">
                {contentData?.amm_left_card_title}
              </h3>
              
              <div className="space-y-6 relative z-10">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-l-4 border-blue-500"
                >
                  <div className="flex items-start gap-3">
                    <FaRocket className="text-blue-500 text-xl mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold text-blue-600">Our Mission:</span> 
                      {contentData?.amm_our_mission_text}
                    </p>
                  </div>
                </motion.div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <FaShieldAlt className="text-green-500" />
                    <h4 className="font-bold text-gray-900">SEBI Registration Details:</h4>
                  </div>
                  
                  <div className="space-y-3">
                    {[
                      contentData?.amm_left_card_rd_text1,
                      contentData?.amm_left_card_rd_text2,
                      contentData?.amm_left_card_rd_text3,
                      contentData?.amm_left_card_rd_text4,
                      contentData?.amm_left_card_rd_text5
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        whileHover={{ x: 5 }}
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-blue-50/50 transition-colors"
                      >
                        <motion.div
                          whileHover={{ scale: 1.2 }}
                          className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"
                        />
                        <span className="text-gray-700">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Why Trusted with Enhanced Animations */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Floating Gem Icon */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 text-4xl text-purple-300 opacity-30"
            >
              <FaGem />
            </motion.div>
            
            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl shadow-lg border border-purple-100 relative overflow-hidden group">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-50/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <div className="flex items-center gap-3 mb-6 relative z-10">
                <motion.div
                  whileHover={{ rotate: 15, scale: 1.1 }}
                  className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center"
                >
                  <FaGem className="text-white text-xl" />
                </motion.div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {contentData?.amm_right_card_title}
                </h3>
              </div>

              <div className="space-y-4 relative z-10">
                {[
                  contentData?.amm_right_card_text1,
                  contentData?.amm_right_card_text2,
                  contentData?.amm_right_card_text3,
                  contentData?.amm_right_card_text4,
                  contentData?.amm_right_card_text5
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      scale: 1.02,
                      boxShadow: "0 10px 20px rgba(147, 51, 234, 0.1)"
                    }}
                    className="flex items-start gap-3 p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 180 }}
                      className="w-6 h-6 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    >
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.2 }}
                        className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                      />
                    </motion.div>
                    <span className="text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>

              {/* Animated Social Media Reach */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                viewport={{ once: true }}
                className="mt-4 pt-4 border-t border-gray-200 relative z-10"
              >
                <div className="flex items-center gap-2 mb-4">
                  <FaBullhorn className="text-purple-500" />
                  <h4 className="font-bold text-gray-900">Our Digital Reach:</h4>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <motion.a
                    whileHover={{ scale: 1.05, y: -3 }}
                    href={contentData?.amm_right_card_dr_link1}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative overflow-hidden bg-gradient-to-r from-pink-500 to-red-500 text-white px-6 py-3 rounded-lg shadow-lg group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative z-10">{contentData?.amm_right_card_dr_text1}</span>
                  </motion.a>
                  
                  <motion.a
                    whileHover={{ scale: 1.05, y: -3 }}
                    href={contentData?.amm_right_card_dr_link2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-lg shadow-lg group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative z-10">{contentData?.amm_right_card_dr_text2}</span>
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Animated Bottom Separator */}
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: "100%" }}
          transition={{ duration: 1.5, delay: 0.8 }}
          viewport={{ once: true }}
          className="h-0.5 bg-gradient-to-r from-transparent via-blue-300 to-transparent mt-16 rounded-full"
        />
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        
        @keyframes pulse-soft {
          0%, 100% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        
        .animation-delay-500 {
          animation-delay: 500ms;
        }
        
        .animation-delay-1000 {
          animation-delay: 1000ms;
        }
        
        .animation-delay-1500 {
          animation-delay: 1500ms;
        }
      `}</style>
    </section>
  );
};

export default AboutCompany;